import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { BefehlFormAuftrag, BefehlFormField } from '../../Generic'
import { TextArea } from '../../../form'
import GrundDialogContext from '../../../../contexts/GrundDialogContext'
import { useContext, useEffect } from 'react'

export interface AuftragX_95FormProps {
  befehlKennung: string
  fieldNamePrefix: string
  readOnly: boolean
  'data-testid': string
  activateEmptyFieldWarnings?: boolean
  withGrundTabelle?: boolean
}

export default function AuftragX_95Form({
  befehlKennung,
  fieldNamePrefix,
  readOnly,
  'data-testid': testId,
  activateEmptyFieldWarnings = false,
  withGrundTabelle
}: Readonly<AuftragX_95FormProps>) {
  const formMethods = useFormContext()
  const { control, formState, setValue, getValues } = formMethods
  const { errors } = formState
  const { t } = useTranslation('ui-befehle')

  const { openGrundDialog, submittedGruende, resetGruende } =
    useContext(GrundDialogContext)

  const auftragFieldName = `${fieldNamePrefix}.enabled`
  const freitextFieldName = `${fieldNamePrefix}.freitext`

  const isAuftragEnabled = formMethods.watch(auftragFieldName)

  const submittedGruendeForFreitext = submittedGruende[freitextFieldName]

  useEffect(() => {
    if (withGrundTabelle && !readOnly) {
      if (submittedGruendeForFreitext || submittedGruendeForFreitext === '') {
        setValue(freitextFieldName, submittedGruendeForFreitext)
      }
    }
  }, [
    withGrundTabelle,
    readOnly,
    setValue,
    freitextFieldName,
    submittedGruendeForFreitext
  ])

  return (
    <BefehlFormAuftrag
      auftragKennung={`${befehlKennung}.95`}
      displayKennung={`${isAuftragEnabled ? befehlKennung : 'x'}.95`}
      fieldName={auftragFieldName}
      control={control}
      readOnly={readOnly}
      testId={testId}
      onChange={(checked) => {
        if (!checked) {
          setValue(freitextFieldName, '')
          resetGruende(freitextFieldName)
        }
      }}
    >
      <span data-testid={`${testId}-text`}>{t('auftraege.x_95.label')}</span>
      <div className="flex-1">
        <div className="flex flex-col">
          <BefehlFormField
            name={freitextFieldName}
            control={control}
            label={``}
            readOnly={readOnly}
            disabled={!isAuftragEnabled}
            activateEmptyWarning={activateEmptyFieldWarnings}
            className="w-full"
            data-testid={`${testId}-freitext`}
            isTextArea={true}
            render={({
              field: freitextField,
              isDisabled: isFreitextDisabled,
              isReadOnly
            }) => (
              <TextArea
                {...freitextField}
                label={t('auftraege.x_95.x_96.label')}
                name={freitextFieldName}
                labelSrOnly={true}
                disabled={isFreitextDisabled}
                readOnly={isReadOnly || withGrundTabelle}
                errors={errors}
                className="w-full"
                data-testid={`${testId}-freitext-inner`}
                onClick={() =>
                  withGrundTabelle &&
                  openGrundDialog({
                    gruende: getValues(freitextFieldName),
                    fieldName: freitextFieldName
                  })
                }
              />
            )}
          />
          <label
            data-testid={`${testId}-freitext-label`}
            className={isAuftragEnabled ? '' : 'text-gray-500 opacity-60'}
          >
            <span data-testid={`${testId}-freitext-label-kennung`}>
              {isAuftragEnabled ? befehlKennung : 'x'}.96
            </span>{' '}
            <span
              data-testid={`${testId}-freitext-label-text`}
              className="italic"
            >
              [{t('auftraege.x_95.x_96.label')}]
            </span>
          </label>
        </div>
      </div>
    </BefehlFormAuftrag>
  )
}
